import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import { Card, CardActionArea, CardContent, Typography } from "@mhome/ui";
import ErrorIcon from "@mui/icons-material/Error";
import {
  disableAutomation,
  enableAutomation,
  listAutomationsByCard,
} from "../service/automation-service";
import { useToast } from "../common/toast";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import MSwitch from "../common/switch";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useWebSocket } from "../ws/websocket";

const AutomationTwinListView = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const cardId = queryParams.get("cardId");
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();
  const customFormat = useCustomFormat();
  const [loading, setLoading] = useState(true);
  const [automations, setAutomations] = useState([]);
  const [currentTime, setCurrentTime] = useState(dayjs());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    handleListAutomations();
  }, [cardId]);

  const handleListAutomations = async () => {
    setLoading(true);
    try {
      if (!cardId) {
        console.error("CardId is undefined");
        setAutomations([]);
        setLoading(false);
        return;
      }

      const resp = await listAutomationsByCard(sendAndWaitWs, cardId);
      if (resp && Array.isArray(resp.automations)) {
        setAutomations(resp.automations);
        setLoading(false);
      } else {
        setAutomations([]);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  const openOverview = (data) => {
    history.push(`/automation/overview?id=${data.id}`);
  };

  const handleEnableDisable = async (event, enableDisable, automationId) => {
    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach(
          (a) => (a.enabled = response.enabled)
        );
        setAutomations(na);
      } else {
        errorToast("Error!");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach(
          (a) => (a.enabled = response.enabled)
        );
        setAutomations(na);
      } else {
        errorToast("Error!");
      }
    }
  };

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <div className="flex-grow m-4 pb-12">
          {!cardId ? (
            <div className="flex flex-col justify-center items-center text-center w-full">
              <Typography variant="h6" color="text.secondary" className="mb-2">
                No card selected
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                className="whitespace-pre-line w-full"
              >
                Please select a card to view its automations
              </Typography>
            </div>
          ) : automations.length !== 0 ? (
            <ResponsiveCardGrid breakpoints={{ xs: 1, sm: 2, md: 3 }}>
              {automations.map((automation, index) => (
                <Card elevation={0} key={index}>
                  <CardActionArea
                    onClick={() => openOverview(automation)}
                    style={{
                      backgroundColor: "hsl(var(--card))",
                      position: "relative",
                    }}
                  >
                    <CardContent className="p-2">
                      <div className="flex justify-between items-center">
                        <Typography
                          variant="body1"
                          component="div"
                          className="line-clamp-3 overflow-hidden text-ellipsis max-h-[4.5em] mb-0"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                          }}
                        >
                          {automation.name}
                        </Typography>
                        <div className="flex gap-2">
                          {!automation.runnable ? (
                            <ErrorIcon
                              style={{
                                color: "red",
                                marginRight: "15px",
                                marginLeft: "15px",
                              }}
                            />
                          ) : (
                            <>
                              <MSwitch
                                checked={automation.enabled}
                                disabled={!automation.runnable}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e, b) =>
                                  handleEnableDisable(e, b, automation.id)
                                }
                                inputProps={{ "aria-label": "controlled" }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      {automation.timestamp !== 0 && (
                        <div>
                          <Typography
                            variant="caption"
                            component="div"
                            className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                            style={{
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              color: "hsl(var(--muted-foreground))",
                            }}
                          >
                            {customFormat(
                              dayjs(automation.timestamp),
                              currentTime
                            )}
                          </Typography>
                        </div>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </ResponsiveCardGrid>
          ) : (
            <div className="flex flex-col justify-center items-center text-center w-full">
              <Typography variant="h6" color="text.secondary" className="mb-2">
                No automations found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                className="whitespace-pre-line w-full"
              >
                Ask assistant to create new one
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                className="whitespace-pre-line w-full"
              >
                Or ask the chatbot
              </Typography>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AutomationTwinListView;
